import { clsx } from 'clsx';
import React, { ReactNode } from 'react';

import styles from './Chip.module.scss';

/**
 * Props for `<Chip>`.
 *
 * Required: `label`. Used as the primitive button across horizon, interval,
 * and chart-type selectors.
 */
export interface ChipProps {
  /** Content rendered between the optional icons. Plain text or any node. */
  label: ReactNode;
  /** Visually highlights the chip and sets `aria-pressed`. */
  isActive?: boolean;
  /** Disables clicks and renders a muted state. */
  disabled?: boolean;
  /** Renders the label uppercase via the chip's typography style. */
  uppercase?: boolean;
  /** Icon rendered to the left of the label. */
  leftIcon?: ReactNode;
  /** Icon rendered to the right of the label (e.g. a chevron). */
  rightIcon?: ReactNode;
  /** Extra class name forwarded to the button element. */
  className?: string;
  /** Click handler. The chip is non-interactive when omitted. */
  onClick?: () => void;
  /** Test selector forwarded as `data-testid`. */
  'data-testid'?: string;
}

/**
 * Internal chartv2 chip: a thin pill-shaped `<button>` that the
 * horizon, interval, and chart-type selectors are built on.
 *
 * Distinct from `@toggle/design-system`'s `Chip` — this one ships only
 * the chartv2 visual treatments (uppercase, active/disabled states)
 * and skips the asset-logo / filter close-button machinery the
 * design-system chip carries.
 */
export const Chip = ({
  label,
  isActive,
  disabled,
  uppercase,
  leftIcon,
  rightIcon,
  className,
  onClick,
  'data-testid': testId,
}: ChipProps) => (
  <button
    type="button"
    className={clsx(
      styles.chip,
      isActive && styles.active,
      disabled && styles.disabled,
      uppercase && styles.upper,
      className
    )}
    onClick={onClick}
    disabled={disabled}
    aria-pressed={isActive}
    data-testid={testId}
  >
    {leftIcon && <span className={styles.icon}>{leftIcon}</span>}
    {label}
    {rightIcon && <span className={styles.icon}>{rightIcon}</span>}
  </button>
);
